import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { KanbanTask, TaskStatus, PriorityTier, TemplateType } from '@/types';
import { Shield, Users, Lock } from 'lucide-react'; // 🛡️ Added for Scope Visuals

interface TaskCardProps {
  task: KanbanTask;
  onMove: (taskId: string, newStatus: TaskStatus) => void;
  onClick?: (task: KanbanTask) => void;
  currentUserId?: string; // 🛡️ Added to verify ownership for personal scope
}

/**
 * 🎴 TaskCard Component v2.0
 * Refactored for Hybrid Scoping: Explicitly distinguishes between Personal and Shared data.
 */
export const TaskCard: React.FC<TaskCardProps> = ({ task, onMove, onClick, currentUserId }) => {
  // 🛰️ DND-KIT SORTABLE HOOK
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: task.id,
    data: {
      task 
    }
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : 1,
  };

  const isGrocery = task.templateType === TemplateType.GROCERY;
  const isAction = task.templateType === TemplateType.ACTION;
  
  /**
   * 🛡️ HYBRID SCOPE LOGIC
   * Personal Partition: Emerald Branding
   * Shared Household Hub: Indigo/Slate Branding
   */
  const isPersonalScope = task.scope === 'personal';
  const isOwner = task.ownerId === currentUserId;

  const getPriorityColor = (priority: PriorityTier | 'none' | undefined) => {
    switch (priority) {
      case 'P1': return 'bg-rose-500 shadow-[0_0_8px_rgba(244,63,94,0.4)]';
      case 'P2': return 'bg-amber-500';
      case 'P3': return isPersonalScope ? 'bg-emerald-500' : 'bg-indigo-500';
      default: return 'bg-slate-300 dark:bg-slate-700';
    }
  };

  return (
    <div 
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={() => onClick?.(task)}
      className={`bg-white dark:bg-slate-900 border rounded-2xl p-4 shadow-sm hover:shadow-md transition-all cursor-grab active:cursor-grabbing mb-3 group relative ${
        isDragging ? 'ring-2 ring-indigo-500 shadow-xl scale-[1.02]' : ''
      } ${
        isPersonalScope 
          ? 'border-emerald-100 dark:border-emerald-900/30' 
          : 'border-slate-200 dark:border-slate-800'
      }`}
    >
      {/* 🛡️ SCOPE INDICATOR: Privacy Verification Badge */}
      <div className="absolute top-4 right-4 flex items-center gap-1.5 opacity-40 group-hover:opacity-100 transition-opacity">
        {isPersonalScope ? (
          <>
            <span className="text-[7px] font-black text-emerald-600 uppercase tracking-widest">Private</span>
            <Shield className="w-3 h-3 text-emerald-500" />
          </>
        ) : (
          <>
            <span className="text-[7px] font-black text-slate-400 uppercase tracking-widest">Shared</span>
            <Users className="w-3 h-3 text-indigo-400" />
          </>
        )}
      </div>

      <div className="flex justify-between items-start mb-2 pr-12">
        <h3 className={`text-sm font-black tracking-tight leading-tight uppercase italic ${
          isPersonalScope ? 'text-emerald-700 dark:text-emerald-400' : 'text-slate-800 dark:text-slate-100'
        }`}>
          {task.title}
        </h3>
        
        {isAction && task.priorityTier && task.priorityTier !== 'none' && (
          <span className={`w-2.5 h-2.5 rounded-full shrink-0 mt-1 ${getPriorityColor(task.priorityTier)}`} />
        )}
      </div>

      {task.description && (
        <p className="text-[11px] font-bold text-slate-500 dark:text-slate-400 line-clamp-2 mb-4 leading-relaxed tracking-tight">
          {task.description}
        </p>
      )}

      {/* --- CONDITIONAL DATA SLOTS --- */}
      <div className="flex flex-wrap gap-2 items-center">
        
        {/* 🎭 MASK: Scope-Aware Identity */}
        {isPersonalScope && (
           <div className="bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 px-2 py-1 rounded-xl text-[9px] font-black uppercase tracking-widest flex items-center gap-1 border border-emerald-100 dark:border-emerald-800">
             <Lock className="w-2.5 h-2.5" />
             <span>Partition Only</span>
           </div>
        )}

        {isGrocery && task.consumption && (
          <div className="bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 px-2 py-1 rounded-xl text-[9px] font-black uppercase tracking-widest flex items-center gap-1 border border-emerald-100 dark:border-emerald-800">
            <span>{task.consumption.remainingQuantity || task.consumption.quantity}</span>
            <span>{task.consumption.unit}</span>
          </div>
        )}

        {isAction && task.energyRequired !== undefined && (
          <div className="bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 px-2 py-1 rounded-xl text-[9px] font-black uppercase tracking-widest flex items-center gap-1 border border-indigo-100 dark:border-indigo-800">
            <span className="opacity-70">⚡</span>
            <span>{task.energyRequired}/10</span>
          </div>
        )}

        {task.recurrence?.isEnabled && (
          <div className="bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-500 px-2 py-1 rounded-xl text-[9px] font-black uppercase tracking-widest flex items-center gap-1">
            <span className="text-[10px]">🔄</span>
            <span>{task.recurrence.frequency}</span>
          </div>
        )}
      </div>

      {/* --- QUICK INTERACTION LAYER --- */}
      <div className="mt-5 pt-3 border-t border-slate-50 dark:border-slate-800/50 flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
        {task.status !== 'history' && (
          <button 
            onPointerDown={(e) => e.stopPropagation()} 
            onClick={(e) => {
              e.stopPropagation();
              const nextStatus: TaskStatus = isGrocery && task.status === 'pantry' ? 'history' : 'done';
              onMove(task.id, nextStatus);
            }}
            className={`text-[9px] font-black uppercase tracking-[0.2em] px-4 py-2 rounded-xl transition-all ${
              isPersonalScope 
                ? 'text-emerald-600 hover:bg-emerald-600 hover:text-white bg-emerald-50 dark:bg-emerald-900/30' 
                : 'text-indigo-600 hover:bg-indigo-600 hover:text-white bg-indigo-50 dark:bg-indigo-900/30'
            }`}
          >
            {isGrocery && task.status === 'pantry' ? 'Archive' : 'Finalize'}
          </button>
        )}
      </div>
    </div>
  );
};